import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { PageHeader, EmptyState, Modal, Field, Button, inr } from "../components/UI";
import { Package, Truck, Plus, Trash2, Calendar, Phone, AlertCircle, CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";

function isoToday() { return new Date().toISOString().slice(0, 10); }

const statusBadge = (s) => {
  const m = {
    pending: "bg-amber-100 text-amber-800",
    printing: "bg-blue-100 text-blue-800",
    delivered: "bg-green-100 text-green-800",
  };
  return m[s] || "bg-slate-100 text-slate-700";
};

export default function PendingOrders() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [dcs, setDcs] = useState([]);
  const [dcOpen, setDcOpen] = useState(false);
  const [dcForm, setDcForm] = useState({
    items: [],
    dc_date: isoToday(),
    planned_dispatch_date: isoToday(),
    vehicle_no: "",
    driver_name: "",
    driver_phone: "",
    notes: "",
  });

  const load = async () => {
    setLoading(true);
    try { setList((await api.get("/pending-orders")).data); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openOrder = async (o) => {
    setSelected(o);
    try {
      const r = await api.get(`/orders/${o.order_id}/deliveries`);
      setDcs(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openDcForm = () => {
    setDcForm({
      items: selected.items.filter((i) => i.pending_qty > 0).map((i) => ({
        product_name: i.product_name,
        quantity: i.pending_qty, // default to full pending
      })),
      dc_date: isoToday(),
      planned_dispatch_date: isoToday(),
      vehicle_no: "",
      driver_name: "",
      driver_phone: "",
      notes: "",
    });
    setDcOpen(true);
  };

  const saveDc = async (e) => {
    e.preventDefault();
    const validItems = dcForm.items.filter((i) => Number(i.quantity) > 0);
    if (validItems.length === 0) { toast.error("Add at least one item quantity"); return; }
    try {
      await api.post("/deliveries", {
        order_id: selected.order_id,
        items: validItems.map((i) => ({ product_name: i.product_name, quantity: Number(i.quantity) })),
        dc_date: dcForm.dc_date,
        planned_dispatch_date: dcForm.planned_dispatch_date || dcForm.dc_date,
        vehicle_no: dcForm.vehicle_no,
        driver_name: dcForm.driver_name,
        driver_phone: dcForm.driver_phone,
        notes: dcForm.notes,
      });
      toast.success("Delivery Challan created");
      setDcOpen(false);
      // Refresh
      load();
      const r = await api.get(`/orders/${selected.order_id}/delivery-summary`);
      setSelected(r.data);
      const d = await api.get(`/orders/${selected.order_id}/deliveries`);
      setDcs(d.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteDc = async (dc) => {
    if (!window.confirm(`Delete DC ${dc.dc_no}?`)) return;
    try {
      await api.delete(`/deliveries/${dc.id}`);
      toast.success("DC deleted");
      load();
      const r = await api.get(`/orders/${selected.order_id}/delivery-summary`);
      setSelected(r.data);
      const d = await api.get(`/orders/${selected.order_id}/deliveries`);
      setDcs(d.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <div className="p-8 text-[#475569]">Loading…</div>;

  return (
    <div className="p-4 lg:p-8 max-w-7xl" data-testid="pending-orders-page">
      <PageHeader
        title="Pending Deliveries"
        subtitle={`${list.length} order${list.length !== 1 ? "s" : ""} with pending quantity · track delivery in parts`}
      />

      {list.length === 0 ? (
        <EmptyState icon={CheckCircle2} title="All caught up!" hint="No orders with pending deliveries. Create new orders from /orders." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left: pending list */}
          <div className="lg:col-span-5 space-y-2 max-h-[80vh] overflow-y-auto" data-testid="pending-list">
            {list.map((o) => {
              const isSel = selected?.order_id === o.order_id;
              return (
                <button
                  key={o.order_id}
                  onClick={() => openOrder(o)}
                  data-testid={`pending-${o.order_no}`}
                  className={`card-flat p-3 w-full text-left transition border-l-4 ${isSel ? "border-[#C1121F] bg-[#FEF2F2]" : "border-transparent hover:border-[#C1121F]"}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-display font-bold text-sm">{o.order_no}</span>
                    <span className={`badge ${statusBadge(o.order_status)} text-[10px]`}>{o.order_status}</span>
                  </div>
                  <div className="text-xs text-[#475569] truncate">{o.customer_name}</div>
                  <div className="mt-2">
                    <div className="flex justify-between text-[11px] mb-1">
                      <span className="text-[#475569]">
                        <span className="font-mono-num font-bold text-green-700">{o.delivered_qty}</span>
                        <span className="text-[#94A3B8]"> / </span>
                        <span className="font-mono-num">{o.total_qty}</span>
                      </span>
                      <span className="font-mono-num font-bold text-[#C1121F]">{o.pending_qty} pending</span>
                    </div>
                    <div className="h-1.5 bg-[#E5E7EB] rounded overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all" style={{ width: `${o.progress_pct}%` }} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right: detail */}
          <div className="lg:col-span-7" data-testid="order-detail">
            {!selected ? (
              <div className="card-flat p-8 text-center text-[#94A3B8] text-sm">
                <Package size={36} className="mx-auto mb-2 opacity-50"/>
                Select an order from the left to view delivery details
              </div>
            ) : (
              <div className="space-y-4">
                <div className="card-flat p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Order</div>
                      <div className="font-display font-bold text-xl">{selected.order_no}</div>
                      <div className="text-sm text-[#475569]">{selected.customer_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Value</div>
                      <div className="font-mono-num font-bold text-lg">{inr(selected.order_total)}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="bg-[#F8F9FA] p-3 rounded text-center">
                      <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Ordered</div>
                      <div className="font-mono-num font-bold text-xl">{selected.total_qty}</div>
                    </div>
                    <div className="bg-green-50 p-3 rounded text-center">
                      <div className="text-[10px] uppercase tracking-wider text-green-700 font-bold">Delivered</div>
                      <div className="font-mono-num font-bold text-xl text-green-700" data-testid="delivered-qty">{selected.delivered_qty}</div>
                    </div>
                    <div className="bg-amber-50 p-3 rounded text-center">
                      <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Pending</div>
                      <div className="font-mono-num font-bold text-xl text-amber-700" data-testid="pending-qty">{selected.pending_qty}</div>
                    </div>
                  </div>

                  <div className="h-2 bg-[#E5E7EB] rounded overflow-hidden mb-3">
                    <div className="h-full bg-gradient-to-r from-green-500 to-green-600" style={{ width: `${selected.progress_pct}%` }} />
                  </div>
                  <div className="text-xs text-[#475569] mb-3 text-center font-mono-num">{selected.progress_pct}% delivered</div>

                  {/* Item-wise breakdown */}
                  <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold mb-2">Items</div>
                  <div className="space-y-1.5">
                    {selected.items.map((it, i) => (
                      <div key={i} className="text-xs flex items-center justify-between gap-2 bg-[#F8F9FA] p-2 rounded">
                        <div className="flex-1 min-w-0 truncate">{it.product_name}</div>
                        <div className="flex-shrink-0 font-mono-num">
                          <span className="text-green-700 font-bold">{it.delivered_qty}</span>
                          <span className="text-[#94A3B8]"> / </span>
                          <span>{it.total_qty}</span>
                          {it.pending_qty > 0 && (
                            <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold">
                              {it.pending_qty} pending
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {selected.pending_qty > 0 && (
                    <Button onClick={openDcForm} data-testid="new-dc-btn" className="mt-4 w-full">
                      <Truck size={14}/> New Delivery Challan
                    </Button>
                  )}
                </div>

                {/* DC History */}
                <div className="card-flat p-5">
                  <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                    <Truck size={16} className="text-[#C1121F]"/> Delivery History · {dcs.length} DC{dcs.length !== 1 ? "s" : ""}
                  </h3>
                  {dcs.length === 0 ? (
                    <div className="text-sm text-[#94A3B8] text-center py-4">No deliveries yet. Create a DC to start.</div>
                  ) : (
                    <div className="space-y-2" data-testid="dc-list">
                      {dcs.map((dc) => (
                        <div key={dc.id} className="border border-[#E5E7EB] rounded p-3" data-testid={`dc-${dc.dc_no}`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="font-display font-bold text-sm">{dc.dc_no}</span>
                              <span className="badge bg-blue-100 text-blue-800 text-[10px]">{dc.status}</span>
                            </div>
                            <button onClick={() => deleteDc(dc)} className="text-[#94A3B8] hover:text-red-600 p-1">
                              <Trash2 size={12}/>
                            </button>
                          </div>
                          <div className="text-xs text-[#475569] flex items-center gap-2 flex-wrap">
                            <span className="flex items-center gap-1"><Calendar size={10}/> {dc.dc_date}</span>
                            <span className="font-mono-num font-bold">· {dc.total_qty} pcs</span>
                            {dc.vehicle_no && <span>· 🚚 {dc.vehicle_no}</span>}
                            {dc.driver_name && <span>· 👤 {dc.driver_name}</span>}
                            {dc.driver_phone && <a href={`tel:${dc.driver_phone}`} className="flex items-center gap-1 text-[#C1121F]"><Phone size={10}/> {dc.driver_phone}</a>}
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/deliveries/${dc.id}/pdf`}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`dc-pdf-${dc.dc_no}`}
                              className="ml-auto flex items-center gap-1 text-[10px] px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-semibold hover:bg-blue-100"
                            >
                              <Download size={10}/> PDF
                            </a>
                          </div>
                          {dc.notes && <div className="text-[11px] text-[#0F172A] mt-1">📝 {dc.notes}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Modal open={dcOpen} onClose={() => setDcOpen(false)} title="New Delivery Challan" maxWidth="max-w-xl">
        {selected && (
          <form onSubmit={saveDc} className="space-y-3">
            <div className="p-3 bg-[#F8F9FA] rounded text-xs">
              <div><b>Order:</b> {selected.order_no} · {selected.customer_name}</div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8] mb-2">Items to Deliver</div>
              <div className="space-y-2">
                {dcForm.items.map((it, idx) => {
                  const max = selected.items.find((x) => x.product_name === it.product_name)?.pending_qty || 0;
                  return (
                    <div key={idx} className="flex items-center gap-2 bg-[#F8F9FA] p-2 rounded">
                      <div className="flex-1 min-w-0 text-xs truncate">{it.product_name}</div>
                      <input
                        data-testid={`dc-item-qty-${idx}`}
                        type="number"
                        min="0"
                        max={max}
                        value={it.quantity}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setDcForm({
                            ...dcForm,
                            items: dcForm.items.map((x, i) => i === idx ? { ...x, quantity: v } : x),
                          });
                        }}
                        className="input-base w-24 font-mono-num"
                      />
                      <span className="text-[10px] text-[#94A3B8] font-mono-num">/ {max}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="DC Date" required>
                <input data-testid="dc-date" type="date" required value={dcForm.dc_date} onChange={(e) => setDcForm({ ...dcForm, dc_date: e.target.value })} className="input-base"/>
              </Field>
              <Field label="Planned Dispatch">
                <input data-testid="dc-dispatch-date" type="date" value={dcForm.planned_dispatch_date} onChange={(e) => setDcForm({ ...dcForm, planned_dispatch_date: e.target.value })} className="input-base"/>
              </Field>
            </div>
            <Field label="Vehicle No">
              <input data-testid="dc-vehicle" value={dcForm.vehicle_no} onChange={(e) => setDcForm({ ...dcForm, vehicle_no: e.target.value })} className="input-base" placeholder="UP-85-AB-1234"/>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Driver Name">
                <input data-testid="dc-driver-name" value={dcForm.driver_name} onChange={(e) => setDcForm({ ...dcForm, driver_name: e.target.value })} className="input-base"/>
              </Field>
              <Field label="Driver Phone">
                <input data-testid="dc-driver-phone" value={dcForm.driver_phone} onChange={(e) => setDcForm({ ...dcForm, driver_phone: e.target.value })} className="input-base" placeholder="9876543210"/>
              </Field>
            </div>
            <Field label="Notes">
              <textarea value={dcForm.notes} onChange={(e) => setDcForm({ ...dcForm, notes: e.target.value })} rows={2} className="input-base" placeholder="e.g. Handover to security at gate"/>
            </Field>

            <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800 flex items-center gap-1.5">
              <AlertCircle size={12}/> Order will auto-move to "delivered" status if all qty is dispatched.
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setDcOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-dc-btn">
                <Plus size={14}/> Create DC
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
